/**
 * РАБОЧИЙ СТОЛ (В1) — ежедневный пульт владельца. Один дашборд-агрегатор из трёх секций
 * поверх GET /api/desk/summary (только чтение): «Требует решения», «Мои задания», «Сигналы».
 * Три пункта раздела (/desk/decisions|my-tasks|signals) ведут сюда; по текущему пути —
 * прокрутка к своей секции. На общих стилях дашборда (styles/main.css, .dash-*).
 */
import { api } from '../api.js';

const SECTION_BY_PATH = {
  '/desk/decisions': 'desk-decisions',
  '/desk/my-tasks': 'desk-my-tasks',
  '/desk/signals': 'desk-signals',
};

export async function initDesk(container) {
  container.innerHTML = '<div class="loading-spinner">Загрузка рабочего стола...</div>';

  let data;
  try {
    data = await api.desk.summary();
  } catch (e) {
    container.innerHTML = `<div class="loading-spinner" style="color:var(--text-danger)">
      Ошибка загрузки: ${esc(e.message)}</div>`;
    return;
  }

  container.innerHTML = `
    <div class="dashboard-root desk-root">
      ${renderSection('desk-decisions', 'Требует решения',
        renderGroups(data.decisions?.groups ?? [], 'Пусто — ничего не требует решения'))}
      ${renderMyTasks(data.my_tasks)}
      ${renderSection('desk-signals', 'Сигналы',
        renderGroups(data.signals?.groups ?? [], 'Пусто — сигналов нет'))}
    </div>`;

  // Прокрутка к секции текущего пункта меню.
  const id = SECTION_BY_PATH[location.pathname.split('?')[0]];
  if (id) {
    requestAnimationFrame(() => container.querySelector('#' + id)?.scrollIntoView({ block: 'start' }));
  }
}

// ── Секция-обёртка (full-width виджет дашборда) ──────────────────────────────

function renderSection(id, title, bodyHtml) {
  return `
    <section class="dash-widget desk-section" id="${id}">
      <header class="dash-widget-header"><h3>${esc(title)}</h3></header>
      <div class="dash-widget-body">${bodyHtml}</div>
    </section>`;
}

// ── Группы (счётчик + топ-список) ────────────────────────────────────────────

function renderGroups(groups, emptyText) {
  const nonEmpty = groups.filter(g => g.count > 0);
  if (!nonEmpty.length) return `<div class="dash-error">${esc(emptyText)}</div>`;
  return nonEmpty.map(renderGroup).join('');
}

function renderGroup(g) {
  const head = g.link
    ? `<a class="desk-group-head" data-route="${g.link}">${esc(g.label)}
         <span class="desk-count">${g.count}</span></a>`
    : `<div class="desk-group-head">${esc(g.label)}
         <span class="desk-count">${g.count}</span></div>`;
  const items = (g.items ?? []).map(renderItem).join('');
  const hidden = g.count - (g.items?.length ?? 0);
  const more = hidden > 0 ? `<div class="dash-priority-meta desk-more">… ещё ${hidden}</div>` : '';
  return `<div class="desk-group">${head}${items}${more}</div>`;
}

function renderItem(it) {
  return `
    <div class="dash-priority-item" data-route="${it.link}">
      <div style="flex:1;min-width:0;">
        <div class="dash-priority-name" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
          ${esc(it.title)}</div>
        ${it.sub ? `<div class="dash-priority-meta">${esc(it.sub)}</div>` : ''}
      </div>
    </div>`;
}

// ── «Мои задания» (сводка, без дублирования «Моего дня») ─────────────────────

function renderMyTasks(mt) {
  let body;
  if (!mt) {
    body = '<div class="dash-error">Нет доступа к задачам</div>';
  } else {
    const active = mt.active
      ? `<div class="dash-priority-item" data-route="${mt.active.link}">
           <div style="flex:1;min-width:0;">
             <div class="dash-priority-name">${esc(mt.active.name)}</div>
             ${mt.active.project_name ? `<div class="dash-priority-meta">${esc(mt.active.project_name)}</div>` : ''}
           </div>
           <span class="dash-priority-meta">активная</span>
         </div>`
      : '<div class="dash-error">Нет активной задачи</div>';
    body = `${active}
      <a class="dash-widget-link desk-myday-link" data-route="/my-day">
        → Мой день${mt.total ? ` (задач: ${mt.total})` : ''}</a>`;
  }
  return renderSection('desk-my-tasks', 'Мои задания', body);
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
